import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient,
  getUserProfileApi, 
  updateUserProfileApi, 
  updateUserAvatarApi, 
  saveGeminiApiKeyApi, 
  deleteGeminiApiKeyApi, 
  getHomeConfigApi,
  getLibraryWalletApi,
  getDashboardStatsApi,
  normalizeUserProfile, customFetch } from '../services/apiService';
import { UserProfile, TopUpPackage } from '../types';
import { hasActiveSession } from '../AuthProvider';


// =========================================================================
// 1. QUERY KEYS
// =========================================================================
export const QUERY_KEYS = {
  userProfile: ['userProfile'] as const,
  generalSettings: ['generalSettings'] as const,
  homeConfig: (key: string) => ['homeConfig', key] as const,
  walletInfo: ['walletInfo'] as const,
  myWalletRequests: (filters: { page: number; limit: number; status?: string; search?: string }) => 
    ['myWalletRequests', filters] as const,
  myWalletTransactions: (filters: { page: number; limit: number; type?: string }) => 
    ['myWalletTransactions', filters] as const,
  topUpPackages: ['topUpPackages'] as const,
  bankInfo: ['bankInfo'] as const,
  favoriteApps: (ids: string[]) => ['favoriteApps', ids] as const,
  adminUsers: (filters: { page: number; limit: number; search?: string; status?: string; gradeLevel?: string; province?: string }) =>
    ['adminUsers', filters] as const,
  adminTopUpRequests: (filters: { page: number; limit: number; search?: string; status?: string; type?: string }) =>
    ['adminTopUpRequests', filters] as const,
  adminPackages: ['adminPackages'] as const,
  adminBankInfo: ['adminBankInfo'] as const,
  adminCoupons: ['adminCoupons'] as const,
  adminConfig: ['adminConfig'] as const,
  adminAdmins: ['adminAdmins'] as const,
  adminLogs: (filters: { page: number; limit: number; search?: string; action?: string }) =>
    ['adminLogs', filters] as const,
  adminLedger: (filters: { page: number; limit: number; search?: string; type?: string }) =>
    ['adminLedger', filters] as const,
  adminDashboardStats: (filter: number | { days?: number; startDate?: string; endDate?: string }) =>
    ['adminDashboardStats', filter] as const,
};

// =========================================================================
// 2. USER PROFILE & AUTH HOOKS
// =========================================================================

export const useUserProfile = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.userProfile,
    queryFn: async () => {
      const res = await getUserProfileApi();
      if (res.success && res.user) {
        return res.user;
      }
      return null;
    },
    enabled: hasAuth,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (formValues: {
      displayName?: string;
      gradeLevel?: string;
      province?: string;
      workplace?: string;
      bio?: string;
    }) => {
      const res = await updateUserProfileApi(formValues);
      if (!res.success) {
        throw new Error(res.message || 'Cập nhật thông tin thất bại');
      }
      return res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
      if (data.user) {
        queryClient.setQueryData(QUERY_KEYS.userProfile, data.user);
      }
    },
  });
};

export const useUpdateAvatarMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { imageBase64?: string; avatar?: string; photoURL?: string; folder?: string }) => {
      const res = await updateUserAvatarApi(payload);
      if (!res.success) {
        throw new Error(res.message || 'Cập nhật ảnh đại diện thất bại');
      }
      return res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
      if (data.user) {
        queryClient.setQueryData(QUERY_KEYS.userProfile, data.user);
      }
    },
  });
};

export const useSaveGeminiApiKeyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (apiKey: string) => {
      return await saveGeminiApiKeyApi(apiKey);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
      if (data.user) {
        queryClient.setQueryData(QUERY_KEYS.userProfile, data.user);
      }
    },
  });
};

export const useDeleteGeminiApiKeyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await deleteGeminiApiKeyApi();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
      if (data.user) {
        queryClient.setQueryData(QUERY_KEYS.userProfile, data.user);
      }
    },
  });
};

// =========================================================================
// 3. GENERAL SETTINGS & CONFIG HOOKS
// =========================================================================

export const useGeneralSettings = () => {
  return useQuery({
    queryKey: QUERY_KEYS.generalSettings,
    queryFn: async () => {
      const res = await getHomeConfigApi('general_settings');
      return res;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useWalletInfo = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.walletInfo,
    queryFn: async () => {
      const res = await getLibraryWalletApi();
      return res;
    },
    enabled: hasAuth,
    staleTime: 60 * 1000,
  });
};

// =========================================================================
// 4. USER WALLET & TRANSACTIONS HOOKS (WITH PAGINATION CACHE)
// =========================================================================

export interface WalletRequestsParams {
  page: number;
  limit: number;
  status?: string;
  search?: string;
}

export interface WalletRequestsResponse {
  data: any[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

export const useMyWalletRequests = (params: WalletRequestsParams) => {
  const hasAuth = hasActiveSession();
  return useQuery<WalletRequestsResponse>({
    queryKey: QUERY_KEYS.myWalletRequests(params),
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      urlParams.append('page', String(params.page));
      urlParams.append('limit', String(params.limit));
      if (params.status && params.status !== 'ALL') {
        urlParams.append('status', params.status);
      }
      if (params.search && params.search.trim()) {
        urlParams.append('search', params.search.trim());
      }

      const res = await apiClient.get(`/topup/my-requests?${urlParams.toString()}`);

      

      const data = res.data;
      const rawList = data.data || data.requests || [];
      const totalCount = data.pagination?.totalCount ?? data.totalCount ?? rawList.length;
      const totalPages = data.pagination?.totalPages ?? data.totalPages ?? (Math.ceil(totalCount / params.limit) || 1);

      return {
        data: rawList,
        pagination: {
          page: params.page,
          limit: params.limit,
          totalCount,
          totalPages,
        },
      };
    },
    enabled: hasAuth,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
};

export interface WalletTransactionsParams {
  page: number;
  limit: number;
  type?: string;
}

export const useWalletTransactions = (params: WalletTransactionsParams) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.myWalletTransactions(params),
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      urlParams.append('page', String(params.page));
      urlParams.append('limit', String(params.limit));
      if (params.type && params.type !== 'ALL') {
        urlParams.append('type', params.type);
      }

      const res = await apiClient.get(`/users/transactions?${urlParams.toString()}`);

      

      const data = res.data;
      const rawList = data.data?.transactions || data.transactions || (Array.isArray(data.data) ? data.data : []);
      const totalCount = data.data?.pagination?.totalCount ?? data.pagination?.totalCount ?? data.totalCount ?? rawList.length;
      const totalPages = data.data?.pagination?.totalPages ?? data.pagination?.totalPages ?? (Math.ceil(totalCount / params.limit) || 1);

      return {
        data: rawList,
        pagination: {
          page: params.page,
          limit: params.limit,
          totalCount,
          totalPages,
        },
      };
    },
    enabled: hasAuth,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCancelTopUpRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const res = await apiClient.post(`/topup/requests/${encodeURIComponent(requestId)}/cancel`, );
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myWalletRequests'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
    },
  });
};

// =========================================================================
// 5. TOPUP PACKAGES & BANK INFO
// =========================================================================

export const useTopUpPackages = (enabled: boolean = true) => {
  return useQuery<TopUpPackage[]>({
    queryKey: QUERY_KEYS.topUpPackages,
    queryFn: async () => {
      const res = await apiClient.get('/topup/packages');
      const data = res.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.packages)) list = data.packages;
      else if (data && data.data && Array.isArray(data.data.packages)) list = data.data.packages;
      else if (data && Array.isArray(data.data)) list = data.data;
      else if (data && Array.isArray(data.items)) list = data.items;
      else if (data && data.data && Array.isArray(data.data.items)) list = data.data.items;
      return Array.isArray(list) ? list : [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useBankInfo = () => {
  return useQuery({
    queryKey: QUERY_KEYS.bankInfo,
    queryFn: async () => {
      const res = await apiClient.get('/topup/bank-info');
      
      const data = res.data;
      return data.bankInfo || data.data || (data.bankName ? data : null) || data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useConfirmTopUpPaymentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const res = await apiClient.post('/topup/confirm-payment', { requestId });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myWalletRequests'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
      queryClient.invalidateQueries({ queryKey: ['adminTopUpRequests'] });
    },
  });
};

// =========================================================================
// 6. FAVORITE APPS HOOKS
// =========================================================================

export const useFavoriteApps = (favoriteIds?: string[]) => {
  const hasAuth = hasActiveSession();
  const ids = favoriteIds || [];
  return useQuery({
    queryKey: QUERY_KEYS.favoriteApps(ids),
    queryFn: async () => {
      if (ids.length === 0) return [];
      const queryIds = ids.join(',');
      const res = await apiClient.get(`/apps?ids=${encodeURIComponent(queryIds)}`);
      const resData = res.data;
      return Array.isArray(resData.data || resData.apps || resData) ? (resData.data || resData.apps || resData) : [];
    },
    enabled: hasAuth && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useToggleFavoriteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ appId, action }: { appId: string; action: 'add' | 'remove' }) => {
      const res = await apiClient.post(`/apps/${encodeURIComponent(appId)}/favorite`, { action });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteApps'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userProfile });
    },
  });
};

// =========================================================================
// 7. ADMIN MANAGEMENT HOOKS (WITH PAGINATION CACHE)
// =========================================================================

export interface AdminUsersParams {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  gradeLevel?: string;
  province?: string;
}

export const useAdminUsers = (params: AdminUsersParams) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminUsers(params),
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      urlParams.append('page', String(params.page));
      urlParams.append('limit', String(params.limit));
      if (params.search && params.search.trim()) urlParams.append('search', params.search.trim());
      if (params.status && params.status !== 'all') urlParams.append('status', params.status);
      if (params.gradeLevel && params.gradeLevel !== 'all') urlParams.append('gradeLevel', params.gradeLevel);
      if (params.province && params.province !== 'all') urlParams.append('province', params.province);

      const res = await apiClient.get(`/users?${urlParams.toString()}`);

      

      const data = res.data;
      const userList = Array.isArray(data) ? data : (data.data || data.users || []);
      const totalCount = data.pagination?.totalCount ?? (Array.isArray(data) ? data.length : (data.totalCount ?? userList.length));
      const totalPages = data.pagination?.totalPages ?? (Math.ceil(totalCount / params.limit) || 1);
      const normalizedUsers = userList.map(normalizeUserProfile);

      return {
        data: normalizedUsers,
        users: normalizedUsers,
        pagination: {
          page: params.page,
          limit: params.limit,
          totalCount,
          totalPages,
        },
        totalRecords: totalCount,
        totalPages,
      };
    },
    enabled: hasAuth,
    placeholderData: keepPreviousData,
    staleTime: 3 * 60 * 1000,
  });
};

export interface AdminTopUpRequestsParams {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  type?: string;
}

export const useAdminTopUpRequests = (params: AdminTopUpRequestsParams) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminTopUpRequests(params),
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      urlParams.append('page', String(params.page));
      urlParams.append('limit', String(params.limit));
      if (params.status && params.status !== 'ALL') urlParams.append('status', params.status);
      if (params.type && params.type !== 'ALL') urlParams.append('type', params.type);
      if (params.search && params.search.trim()) urlParams.append('search', params.search.trim());

      const res = await apiClient.get(`/topup/admin/requests?${urlParams.toString()}`);

      

      const data = res.data;
      const reqList = data.data || data.requests || [];
      const totalCount = data.pagination?.totalCount ?? data.totalCount ?? reqList.length;
      const totalPages = data.pagination?.totalPages ?? data.totalPages ?? (Math.ceil(totalCount / params.limit) || 1);

      return {
        data: reqList,
        requests: reqList,
        pagination: {
          page: params.page,
          limit: params.limit,
          totalCount,
          totalPages,
        },
        totalRecords: totalCount,
        totalPages,
      };
    },
    enabled: hasAuth,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
};

export const useAdminPackages = () => {
  const hasAuth = hasActiveSession();
  return useQuery<TopUpPackage[]>({
    queryKey: QUERY_KEYS.adminPackages,
    queryFn: async () => {
      const res = await apiClient.get('/topup/admin/packages');
      
      const data = res.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.packages)) list = data.packages;
      else if (data && data.data && Array.isArray(data.data.packages)) list = data.data.packages;
      else if (data && Array.isArray(data.data)) list = data.data;
      else if (data && Array.isArray(data.items)) list = data.items;
      else if (data && data.data && Array.isArray(data.data.items)) list = data.data.items;
      else if (data && typeof data === 'object' && Object.keys(data).length > 0 && !data.success && !data.message) {
         // Maybe it's a map?
         list = Object.values(data);
      }
      return Array.isArray(list) ? list : [];
    },
    enabled: hasAuth,
    staleTime: 3 * 60 * 1000,
  });
};

export const useAdminTopUpPackages = useAdminPackages;

export const useAdminBankInfo = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminBankInfo,
    queryFn: async () => {
      const res = await apiClient.get('/topup/admin/bank-info');
      
      const data = res.data;
      return data.bankInfo || data.data || (data.bankName ? data : null) || data;
    },
    enabled: hasAuth,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAdminCoupons = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminCoupons,
    queryFn: async () => {
      const res = await apiClient.get('/topup/admin/coupons');
      
      const data = res.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.packages)) list = data.packages;
      else if (data && data.data && Array.isArray(data.data.packages)) list = data.data.packages;
      else if (data && Array.isArray(data.data)) list = data.data;
      else if (data && Array.isArray(data.items)) list = data.items;
      else if (data && data.data && Array.isArray(data.data.items)) list = data.data.items;
      else if (data && typeof data === 'object' && Object.keys(data).length > 0 && !data.success && !data.message) {
         // Maybe it's a map?
         list = Object.values(data);
      }
      return Array.isArray(list) ? list : [];
    },
    enabled: hasAuth,
    staleTime: 3 * 60 * 1000,
  });
};

export const useAdminAppConfig = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminConfig,
    queryFn: async () => {
      const res = await apiClient.get('/config');
      
      const data = res.data;
      return data.config || data.data || data || {};
    },
    enabled: hasAuth,
    staleTime: 5 * 60 * 1000,
  });
};

// =========================================================================
// 8. ADMIN MUTATIONS
// =========================================================================

export const useAdminApproveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, note }: { requestId: string; note?: string }) => {
      const res = await apiClient.post(`/topup/admin/approve/${requestId}`, { note: note || 'Duyệt nạp tiền thành công' });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTopUpRequests'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
};

export const useAdminRejectRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) => {
      const res = await apiClient.post(`/topup/admin/reject/${requestId}`, { reason });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTopUpRequests'] });
    },
  });
};

export const useAdminSavePackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ isEdit, pkgId, payload }: { isEdit: boolean; pkgId?: string; payload: any }) => {
      const res = isEdit ? await apiClient.put(`/topup/admin/packages/${pkgId}`, payload) : await apiClient.post('/topup/admin/packages', payload);
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminPackages });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.topUpPackages });
    },
  });
};

export const useAdminDeletePackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pkgId: string) => {
      const res = await apiClient.delete(`/topup/admin/packages/${pkgId}`);
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminPackages });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.topUpPackages });
    },
  });
};

export const useAdminSaveBankInfoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post('/topup/admin/bank-info', payload);
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminBankInfo });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankInfo });
    },
  });
};

export const useAdminSaveCouponMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ isEdit, couponId, payload }: { isEdit: boolean; couponId?: string; payload: any }) => {
      const res = isEdit ? await apiClient.put(`/topup/admin/coupons/${couponId}`, payload) : await apiClient.post('/topup/admin/coupons', payload);
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminCoupons });
    },
  });
};

export const useAdminDeleteCouponMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (couponId: string) => {
      const res = await apiClient.delete(`/topup/admin/coupons/${couponId}`);
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminCoupons });
    },
  });
};

export const useAdminToggleCouponMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ couponId, isActive }: { couponId: string; isActive: boolean }) => {
      const res = await apiClient.put(`/topup/admin/coupons/${couponId}`, { isActive });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminCoupons });
    },
  });
};

export const useAdminList = () => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminAdmins,
    queryFn: async () => {
      const res = await apiClient.get('/admins');
      
      const data = res.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.packages)) list = data.packages;
      else if (data && data.data && Array.isArray(data.data.packages)) list = data.data.packages;
      else if (data && Array.isArray(data.data)) list = data.data;
      else if (data && Array.isArray(data.items)) list = data.items;
      else if (data && data.data && Array.isArray(data.data.items)) list = data.data.items;
      else if (data && typeof data === 'object' && Object.keys(data).length > 0 && !data.success && !data.message) {
         // Maybe it's a map?
         list = Object.values(data);
      }
      return Array.isArray(list) ? list : [];
    },
    enabled: hasAuth,
    staleTime: 3 * 60 * 1000,
  });
};

export const useAdminLogs = (filters: {
  page: number;
  limit: number;
  search?: string;
  action?: string;
}) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminLogs(filters),
    queryFn: async () => {
      const urlParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: filters.limit.toString(),
      });
      if (filters.search && filters.search.trim()) {
        urlParams.set('search', filters.search.trim());
      }
      if (filters.action && filters.action !== 'all') {
        urlParams.set('action', filters.action);
      }

      const res = await apiClient.get(`/logs?${urlParams.toString()}`);
      
      const data = res.data;
      return {
        data: data.logs || data.data || [],
        pagination: data.pagination || {
          page: filters.page,
          limit: filters.limit,
          totalCount: (data.logs || []).length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    },
    placeholderData: keepPreviousData,
    enabled: hasAuth,
    staleTime: 1 * 60 * 1000,
  });
};

export const usePromoteAdminMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, userId }: { email?: string; userId?: string }) => {
      const res = await apiClient.post('/admins/promote', { email, userId });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminAdmins });
      queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
};

export const useDemoteAdminMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const res = await apiClient.post('/admins/demote', { userId });
      const data = res.data;
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminAdmins });
      queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
};

export interface AdminLedgerFilters {
  page: number;
  limit: number;
  search?: string;
  type?: string;
}

export const useAdminLedger = (filters: AdminLedgerFilters) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminLedger(filters),
    queryFn: async () => {
      const urlParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: filters.limit.toString(),
        all: 'true',
      });
      if (filters.search && filters.search.trim()) {
        urlParams.set('search', filters.search.trim());
      }
      if (filters.type && filters.type !== 'all' && filters.type !== 'ALL') {
        urlParams.set('type', filters.type);
      }

      const res = await apiClient.get(`/users/transactions?${urlParams.toString()}`);
      
      const data = res.data;
      const rawList = data.data?.transactions || data.transactions || (Array.isArray(data.data) ? data.data : []);
      const totalCount = data.data?.pagination?.totalCount ?? data.pagination?.totalCount ?? data.totalCount ?? rawList.length;
      const totalPages = data.data?.pagination?.totalPages ?? data.pagination?.totalPages ?? (Math.ceil(totalCount / filters.limit) || 1);

      return {
        data: rawList,
        pagination: {
          page: filters.page,
          limit: filters.limit,
          totalCount,
          totalPages,
          hasNextPage: filters.page < totalPages,
          hasPrevPage: filters.page > 1,
        },
      };
    },
    placeholderData: keepPreviousData,
    enabled: hasAuth,
    staleTime: 1 * 60 * 1000,
  });
};

// =========================================================================
// 8. ADMIN DASHBOARD STATS HOOK
// =========================================================================
export type AdminDashboardFilter = number | { days?: number; startDate?: string; endDate?: string };

export const useAdminDashboardStats = (filter: AdminDashboardFilter = 7) => {
  const hasAuth = hasActiveSession();
  return useQuery({
    queryKey: QUERY_KEYS.adminDashboardStats(filter),
    queryFn: () => getDashboardStatsApi(filter),
    enabled: hasAuth,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
};


